// src/pages/LoginPage.jsx
import { useState, useEffect } from "react";
import useAuthStore from "../../store/authStore";
import { useNavigate, useSearchParams } from "react-router-dom";
import RandomQuote from "../../components/RandomQuote/RandomQuote";
import {
  acceptOrganizationInvite,
  validateInviteToken,
} from "../../api/organizations/organizations";
import BrandTitle from "../../components/BrandTitle/BrandTitle";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState(null);
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);
  const [searchParams] = useSearchParams();

  const login = useAuthStore((state) => state.login);
  const refreshUserData = useAuthStore((state) => state.refreshUserData);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const redirect = searchParams.get("redirect");
  const inviteToken = searchParams.get("token");

  // Fetch invite email if there's an invite token
  useEffect(() => {
    const fetchInviteInfo = async () => {
      if (redirect === "accept-invite" && inviteToken) {
        try {
          const validation = await validateInviteToken(inviteToken);
          setInviteEmail(validation.email);
          // Pre-fill the email field with the invited email
          setEmail(validation.email);
        } catch (err) {
          console.error("Failed to validate invite:", err);
        }
      }
    };

    fetchInviteInfo();
  }, [redirect, inviteToken]);

  // If user is already logged in and there's an invite token, auto-accept
  useEffect(() => {
    const autoAcceptInvite = async () => {
      // Prevent running multiple times or if already accepting
      if (isAcceptingInvite) return;

      if (user && redirect === "accept-invite" && inviteToken) {
        setIsAcceptingInvite(true);
        try {
          console.log("Auto-accepting invite for logged-in user:", user.id);
          await acceptOrganizationInvite(inviteToken, {
            existingUserId: user.id,
          });
          console.log("Auto-accept successful, refreshing data...");

          // Wait a bit for backend to process
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Use the new refreshUserData method
          const refreshed = await refreshUserData();
          console.log("Auto-accept - Data refresh result:", refreshed);

          // Navigate to tasks
          navigate("/boards");
        } catch (err) {
          console.error("Auto-accept error:", err);
          setError(err.message || i18n.t("auth.acceptInviteFailed"));
          setIsAcceptingInvite(false);
        }
      }
    };

    autoAcceptInvite();
  }, [
    user,
    redirect,
    inviteToken,
    navigate,
    isAcceptingInvite,
    refreshUserData,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsAcceptingInvite(true);
    const { success, message } = await login(email, password);
    if (success) {
      // If there's an invite token, accept it after login
      if (redirect === "accept-invite" && inviteToken) {
        try {
          const authStore = useAuthStore.getState();
          const currentUser = authStore.user;

          console.log("Accepting invite for user:", currentUser.id);
          await acceptOrganizationInvite(inviteToken, {
            existingUserId: currentUser.id,
          });
          console.log("Invite accepted, refreshing user data...");

          // Wait a bit for backend to process
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Use the new refreshUserData method
          const refreshed = await refreshUserData();
          console.log("Data refresh result:", refreshed);
        } catch (err) {
          console.error("Failed to accept invitation:", err);
          setError(err.message || t("auth.acceptInviteFailed"));
          setIsAcceptingInvite(false);
          return;
        }
      }
      navigate("/tasks");
    } else {
      setError(message || t("auth.login.failed"));
      setIsAcceptingInvite(false);
    }
  };

  return (
    <div className="authentication-view">
      <div className="left-wrapper">
        <div className="title">
          <BrandTitle fontSize={40} className="brand-title-login" />
        </div>
        <div className="quote">
          <RandomQuote />
        </div>
      </div>
      <div className="right-wrapper">
        <div className="title">{t("auth.login.title")}</div>
        {redirect === "accept-invite" && (
          <p
            style={{
              marginBottom: "20px",
              fontSize: "14px",
              color: "var(--blue-button-color)",
            }}
          >
            {inviteEmail
              ? t("auth.login.inviteWithEmail", { email: inviteEmail })
              : t("auth.login.invite")}
          </p>
        )}
        <form
          onSubmit={handleSubmit}
          autoComplete={redirect === "accept-invite" ? "off" : "on"}
        >
          <input
            className="flow-form"
            type="email"
            placeholder={t("common.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete={redirect === "accept-invite" ? "off" : "email"}
            required
          />
          <br />
          <input
            className="flow-form"
            type="password"
            placeholder={t("common.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={
              redirect === "accept-invite" ? "off" : "current-password"
            }
            required
          />
          <br />
          <button type="submit" className="flow-button">
            {t("auth.login.submit")}
          </button>
        </form>
        {error && (
          <span
            style={{
              color: "var(--error-color)",
              marginTop: "10px",
              marginBottom: "-10px",
            }}
          >
            {error}
          </span>
        )}
        <p>
          <a className="link" href="/forgot-password">
            {t("auth.login.forgotPassword")}
          </a>
        </p>
        <a className="link" style={{ marginTop: "-10px" }} href="/signup">
          {t("auth.login.createAccount")}
        </a>
        <div className="logo">
          <img
            src="/somigo_logo.svg"
            alt={t("auth.logoAlt")}
            width="100"
            height="100"
          />
        </div>
      </div>
    </div>
  );
}
